import React from "react";
import SkeletonLoader from "@/components/ui/SkeletonLoader";

/**
 * ScoreComparison — sibling of ScoreCard for the GEO Comparison page.
 *
 * Instead of a single value + change, it shows the same metric side by side for
 * two periods (valueA / valueB) with an optional percentage change between them.
 */
const ScoreComparison = ({
  title,
  icon,
  valueA,
  valueB,
  labelA = null,
  labelB = null,
  change = null,
  isLoaded = false,
  error = null,
  size = "md",
  iconColor = "var(--brand-color-1)",
  iconBgColor = "var(--bg-surface)",
  cardBgColor = "var(--bg-elevated)",
  titleColor = "var(--text-secondary)",
  valueAColor = "var(--period-a-color)",
  valueBColor = "var(--period-b-color)",
  className = "",
  tooltipText = null,
}) => {
  const sizeConfig = {
    sm: {
      cardPadding: "p-2",
      iconSize: "20px",
      titleSize: "fs-6",
      valueFontSize: "0.85rem",
      changeSize: "small",
      minHeight: "80px",
    },
    md: {
      cardPadding: "p-3",
      iconSize: "24px",
      titleSize: "fs-6",
      valueFontSize: "1.05rem",
      changeSize: "small",
      minHeight: "120px",
    },
    lg: {
      cardPadding: "p-3",
      iconSize: "28px",
      titleSize: "fs-5",
      valueFontSize: "1.25rem",
      changeSize: "fs-6",
      minHeight: "120px",
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  const cardStyle = {
    backgroundColor: cardBgColor,
    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
    borderColor: "var(--border)",
    minHeight: config.minHeight,
  };

  const renderIcon = () => (
    <div
      className="d-flex align-items-center justify-content-center me-3"
      style={{
        width: "40px",
        height: "40px",
        backgroundColor: iconBgColor,
        minWidth: "40px",
        borderRadius: "30%",
      }}
      aria-hidden="true"
    >
      {typeof icon === "string" ? (
        <i
          className={icon}
          style={{ color: iconColor, fontSize: config.iconSize }}
        />
      ) : (
        <span style={{ color: iconColor, fontSize: config.iconSize }}>
          {icon}
        </span>
      )}
    </div>
  );

  const renderHeader = () => (
    <div className="d-flex align-items-center mb-3">
      {renderIcon()}
      <span
        className={`fw-normal ${config.titleSize} text-break`}
        style={{ color: titleColor }}
      >
        {title}
        {tooltipText && (
          <i
            className="bi bi-info-circle ms-1"
            title={tooltipText}
            style={{
              cursor: "pointer",
              fontSize: "0.85em",
              color: "var(--text-secondary)",
            }}
          ></i>
        )}
      </span>
    </div>
  );

  if (error) {
    return (
      <div
        className={`card ${config.cardPadding} ${className} card-radius d-flex align-items-center justify-content-center`}
        style={cardStyle}
      >
        <div className="w-100 text-center">
          <div
            className="alert alert-danger mb-0 text-break"
            role="alert"
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.4",
              padding: "0.5rem 0.75rem",
            }}
          >
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="text-wrap">
              An error occurred. Please try again.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`card ${config.cardPadding} ${className} card-radius`}
        style={cardStyle}
      >
        {renderHeader()}
        <div
          className="d-flex align-items-end justify-content-between"
          style={{ marginLeft: "8px" }}
        >
          <SkeletonLoader width="35%" height="28px" />
          <SkeletonLoader width="20%" height="16px" />
          <SkeletonLoader width="35%" height="28px" />
        </div>
      </div>
    );
  }

  const numChange =
    change !== null && change !== undefined
      ? typeof change === "number"
        ? change
        : parseFloat(change)
      : null;
  const isValidChange = numChange !== null && !Number.isNaN(numChange);

  const formatChange = (changeValue) => {
    if (changeValue === null || changeValue === undefined) return null;
    const num =
      typeof changeValue === "number" ? changeValue : parseFloat(changeValue);
    if (Number.isNaN(num)) return null;
    const sign = num >= 0 ? "+" : "";
    return `${sign}${num.toFixed(1)}%`;
  };

  const getChangeColor = (changeValue) => {
    if (changeValue === null || changeValue === undefined)
      return "var(--text-muted)";
    const num =
      typeof changeValue === "number" ? changeValue : parseFloat(changeValue);
    if (Number.isNaN(num)) return "var(--text-muted)";
    return num >= 0 ? "var(--indicator-green)" : "var(--error)";
  };

  const renderValueBlock = (label, value, align, color) => (
    <div
      className={`d-flex flex-column ${align}`}
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      {label && (
        <span
          className="text-nowrap"
          style={{
            fontSize: "0.7rem",
            fontWeight: 500,
            color: "var(--text-secondary)",
            textTransform: "uppercase",
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </span>
      )}
      <span
        className="fw-bold mb-0 text-nowrap"
        style={{
          color,
          fontSize: config.valueFontSize,
          lineHeight: "1.2",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div
      className={`card ${config.cardPadding} ${className} card-radius`}
      style={cardStyle}
    >
      {renderHeader()}
      <div
        className="d-flex flex-nowrap align-items-end justify-content-between"
        style={{ gap: "0.35rem" }}
        data-testid="score-comparison-value-row"
      >
        {renderValueBlock(labelA, valueA, "align-items-start text-start", valueAColor)}

        {isValidChange && (
          <span
            className={`${config.changeSize} fw-medium d-flex align-items-center text-nowrap`}
            style={{
              color: getChangeColor(change),
              lineHeight: "1.2",
              marginBottom: "2px",
            }}
          >
            <i
              className={`me-1 ${
                numChange >= 0
                  ? "bi bi-arrow-up-right"
                  : "bi bi-arrow-down-right"
              }`}
            ></i>
            {formatChange(change)}
          </span>
        )}

        {renderValueBlock(labelB, valueB, "align-items-end text-end", valueBColor)}
      </div>
    </div>
  );
};

export default ScoreComparison;
